/**
 * localStorage round-trip helpers for allow-listed string preferences
 * (e.g. sort orders, filter modes). Used by `TabbedBasketManager` so the
 * user's basket sort/filter selections survive route navigation without
 * leaking a non-allowed stored value into the type system.
 *
 * Guards:
 * - SSR safety (`typeof window === 'undefined'`) — returns the fallback.
 * - localStorage throws in Safari private mode → swallowed.
 * - Stale/invalid stored value (not in the allow-list) → fallback.
 */

export function readPersistedValue<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = localStorage.getItem(key);
    if (stored && (allowed as readonly string[]).includes(stored)) {
      return stored as T;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

export function writePersistedValue(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage.setItem can throw in Safari private mode and on quota
    // overflow — both are non-fatal for preference persistence.
  }
}
